// V2 Article Promote — simplified one-tap queue flow.
import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Center,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft, IconRocket } from '@tabler/icons-react';
import useApi from '../hooks/useApi';
import {
  fetchArticle,
  fetchArticleConnectedCount,
  promoteArticle,
} from '../services/api';

export default function AviaryV2ArticlePromotePage() {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const { slug } = useParams();
  const back = location.state?.back || { path: `/v2/articles/${slug}`, label: 'Article' };

  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);
  const [connectedCount, setConnectedCount] = useState(null);
  const [promoting, setPromoting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchArticle(api, slug),
      fetchArticleConnectedCount(api, slug),
    ])
      .then(([a, countData]) => {
        if (cancelled) return;
        setArticle(a);
        setConnectedCount(Number(countData?.count || 0));
      })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.error || err.message); });
    return () => { cancelled = true; };
  }, [api, slug]);

  async function submit() {
    if (!connectedCount) {
      navigate('/v2/articles');
      return;
    }
    setPromoting(true);
    try {
      const result = await promoteArticle(api, slug);
      notifications.show({
        title: `Queued ${result.promoted} drafts`,
        message: result.skipped
          ? `${result.skipped} recipients already had a pending draft for this article.`
          : 'Drafts land in your approval queue — Today → Messages waiting.',
        color: 'sage',
      });
      navigate('/v2/articles');
    } catch (err) {
      notifications.show({
        title: 'Promote failed',
        message: err.response?.data?.error || err.message,
        color: 'red',
      });
    } finally {
      setPromoting(false);
    }
  }

  if (error) {
    return (
      <Center py={80} px="lg">
        <Stack gap="sm" align="center">
          <Text fw={600} c="red">Couldn't load promote flow</Text>
          <Text size="sm" c="dimmed">{error}</Text>
        </Stack>
      </Center>
    );
  }
  if (!article || connectedCount == null) {
    return (
      <Center py={80}>
        <Loader color="sage" />
      </Center>
    );
  }

  return (
    <Box style={{ padding: '32px 24px 120px', maxWidth: 720, margin: '0 auto' }}>
      <Stack gap="lg">
        <UnstyledButton
          onClick={() => navigate(back.path)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--aviary-muted)',
            fontSize: 13,
            width: 'fit-content',
          }}
        >
          <IconArrowLeft size={14} /> Back to {back.label}
        </UnstyledButton>

        <Stack gap={4}>
          <Text fw={700} style={{ color: 'var(--aviary-ink)', fontSize: 28 }}>
            Promote article
          </Text>
          <Text size="sm" style={{ color: 'var(--aviary-muted)' }}>
            <strong>{article.title}</strong>
          </Text>
        </Stack>

        <Paper p="lg" radius="md" withBorder>
          <Stack gap="md">
            <Text size="sm" style={{ color: 'var(--aviary-muted)', lineHeight: 1.6 }}>
              We'll write a personal note to each of your {connectedCount} connected {connectedCount === 1 ? 'person' : 'people'} and drop them in your approval queue so you can look them over before anything goes out.
            </Text>
            {connectedCount === 0 && (
              <Text size="sm" c="dimmed">
                Nobody's connected yet — get some invitations accepted first. <Link to="/v2/board">Open board</Link>
              </Text>
            )}
          </Stack>
        </Paper>

        <Group justify="flex-end" gap="sm">
          <Button variant="default" onClick={() => navigate(back.path)} disabled={promoting}>
            Cancel
          </Button>
          <Button
            color="sage"
            leftSection={<IconRocket size={16} />}
            onClick={submit}
            loading={promoting}
          >
            Ok →
          </Button>
        </Group>
      </Stack>
    </Box>
  );
}
